<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            {{ model.id ? 'Edit User' : 'Add User to Organizational Unit' }}
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="model.id" :closeable="false">
                Editing user <strong class="font-bold">{{ model.email }}</strong>
                <span v-if="originalRoleLabel" class="font-bold lowercase"> - {{ originalRoleLabel }}</span>
            </vx-alert>
            <vx-alert v-else :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                </template>
                Adding user to
                <strong v-if="props.orgUnitName" class="font-bold">{{ props.orgUnitName }}</strong> organizational unit
            </vx-alert>
            <vx-alert
                v-if="!model.id && willUseSso && !inviteBlocked"
                color="info"
                :closeable="false"
                data-test="sso-invite-hint"
            >
                They'll log in with SSO.
            </vx-alert>

            <vx-alert v-if="changingOwner && owner" data-test="changing-owner-alert" :closeable="false" color="danger">
                The current owner of <strong>{{ props.orgUnitName }}</strong> is <strong>{{ owner.name }}</strong
                >. By changing this user to an owner, you will transfer ownership to them.
            </vx-alert>
            <div v-if="!model.id">
                <vx-label>Email</vx-label>

                <vx-selectable
                    :options="users"
                    :loading="searching"
                    relativeDropdown
                    :reduce="(option) => option.email"
                    :createOption="
                        (option) =>
                            typeof option === 'string'
                                ? {
                                      email: option,
                                  }
                                : { ...option }
                    "
                    @search="search"
                    v-model="model.email"
                    data-test="email"
                    label="email"
                    size="lg"
                    taggable
                    :error="inviteBlocked ? blockedMessage : displayFirstError(validate.email.$errors)"
                >
                </vx-selectable>
            </div>
            <div>
                <vx-label>
                    Organizational Unit Role
                    <template v-slot:description>
                        View
                        <a :href="permissionChartLink" target="_blank" rel="noopener noreferrer">permission chart</a>
                        to see what each type has access to.
                    </template>
                </vx-label>

                <vx-selectable
                    v-model="model.role"
                    size="lg"
                    data-test="select-role"
                    relativeDropdown
                    :reduce="(option) => option.value"
                    :options="orgUnitRolesOptions()"
                    :clearable="false"
                    :error="displayFirstError(validate.role.$errors)"
                >
                </vx-selectable>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click.prevent="submit()"
                    :loading="submitting"
                    :disabled="inviteBlocked"
                    data-test="submit-btn"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    {{ model.id ? 'Update' : 'Add' }}
                    <font-awesome-icon widthAuto :icon="model.id ? faCircleCheck : faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { VxAlert, VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { orgUnitInvitationsClient, orgUnitUsersClient, usersClient } from '~/services';
import { HTTP_OK, HTTP_CREATED } from '~/support/HttpStatuses';
import { faCircleCheck, faCirclePlus, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DEFAULT_ORG_UNIT_ROLE, ORG_UNIT_USER_ROLES, orgUnitRolesOptions } from '~/constants/roles';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { email, required, requiredIf } from '@vuelidate/validators';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useInviteEmailDomains } from '~/composables/useInviteEmailDomains';
import debounce from '~/utils/debounce';
import { permissionChartLink } from '~/constants/groups';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = defineModel({
    type: Object,
    required: true,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
    orgUnitName: {
        type: String,
        required: false,
    },
});

const { $externalResults, catchError } = useFormRequestHandler();

const validate = useVuelidate(
    {
        email: {
            requiredIf: requiredIf(() => model.value.id === null),
            email: email,
        },
        role: {
            required: required,
        },
    },
    computed(() => ({ role: model.value.role, email: model.value.email })),
    { $externalResults }
);

const emit = defineEmits(['added', 'updated', 'invited']);

const submitting = ref(false);
const users = ref([]);
const searching = ref(false);
const originalRoleLabel = ref('');
const search = debounce((search) => {
    searching.value = true;
    usersClient
        .listUsers(props.teamId, {
            query: search,
        })
        .then((response) => {
            users.value = response.data.data;
            searching.value = false;
        });
}, 200);

const { blocked, blockedMessage, willUseSso } = useInviteEmailDomains(
    props.teamId,
    computed(() => model.value.email ?? '')
);
const inviteBlocked = computed(
    () => !model.value.id && !users.value.some((user) => user.email === model.value.email) && blocked.value
);

const updateVisible = (isVisible) => {
    validate.value.$reset();
    visible.value = isVisible;

    if (!isVisible) {
        model.value = {
            id: null,
            name: '',
            email: '',
            role: DEFAULT_ORG_UNIT_ROLE,
        };
    }
};

onMounted(() => {
    if (!model.value?.id) {
        search();
    }
});

const owner = ref();

const isOrgUnitOwner = computed(() => {
    return owner.value?.id === model.value.id;
});

const changingOwner = computed(() => {
    return !isOrgUnitOwner.value && model.value.role === 'owner';
});

const getOrgUnitOwner = async () => {
    try {
        const response = await orgUnitUsersClient.listOrgUnitUsers(props.teamId, props.orgUnitId, {
            role: 'owner',
        });

        owner.value = response.data?.data?.[0];
    } catch (e) {
        console.error(catchRequestError(e));
    }
};

watch(
    visible,
    (isVisible) => {
        if (isVisible && model.value?.id) {
            originalRoleLabel.value = ORG_UNIT_USER_ROLES[model.value.role] || '';
        }

        if (isVisible) {
            getOrgUnitOwner();
        }
    },
    { immediate: true }
);

const submit = async () => {
    if (inviteBlocked.value) {
        return;
    }

    const isValid = await validateRules(validate);
    if (!isValid || submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const existingUser = model.value?.id
            ? model.value
            : users.value.find((user) => user.email === model.value.email);

        if (existingUser?.id) {
            const response = await orgUnitUsersClient.upsertOrgUnitUser(
                props.teamId,
                props.orgUnitId,
                existingUser.id,
                {
                    role: model.value.role,
                }
            );

            if (response.status === HTTP_CREATED) {
                emit('added', { ...model.value, id: existingUser.id, name: existingUser.name });
                toasted.success('New User added successfully');
            } else if (response.status === HTTP_OK) {
                emit('updated', { ...model.value, id: existingUser.id, name: existingUser.name });
                toasted.success('User updated successfully');
            }
        } else {
            await orgUnitInvitationsClient.sendInvitation(props.teamId, props.orgUnitId, {
                email: model.value.email,
                role: model.value.role,
            });

            emit('invited', { ...model.value });
            toasted.success('New User invited successfully');
        }

        updateVisible(false);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
